/**
 * features/finance/ManualInvoice.tsx
 * ------------------------------------------------------------------
 * Ad-hoc invoice for a single student — for mid-term charges that
 * aren't part of the termly billing run or the joining invoice
 * (exam fees, a replacement textbook, a trip, a fine, etc.).
 *
 * Lines can be added from the fee catalog (one tap) or typed in by
 * hand. The invoice gets its own unique id so it never collides with
 * the student's term invoice (`${year}_${term}_${studentId}`), and it
 * shows up immediately on the student's statement and in Payments.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Plus, Trash2, Search, ReceiptText, CheckCircle2, UserRound, Download, Mail, Settings, AlertTriangle } from 'lucide-react';
import { AccentButton, GradientHero, SoftCard, glassFieldClass } from '@/features/finance/ui';
import { NumberField } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { deliverInvoice } from '@/lib/invoiceDelivery';
import { buildInvoicePdf } from '@/lib/invoicePdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { getPrimaryContact } from '@/lib/admissions';
import { SCHOOL, termLabel, currentTermKey, type TermKey } from '@/lib/school.config';
import {
  assembleInvoice, formatInvoiceNo, fmtPula, round2,
  FEE_CATEGORY_LABELS, type FeeCategory, type InvoiceLine, type FeeItem, type Invoice,
} from '@/lib/financeCore';

const TERMS = Object.keys(SCHOOL.terms) as TermKey[];
const plusDays = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function ManualInvoiceScreen() {
  const acad = useData();
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email };

  const [sub, setSub] = useState<'new' | 'settings'>('new');
  const [savingToggle, setSavingToggle] = useState(false);
  const emailEnabled = data.settings.invoiceEmailEnabled !== false; // default ON
  const [studentId, setStudentId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [year, setYear] = useState(SCHOOL.selectableYears[0] ?? String(new Date().getFullYear()));
  const [term, setTerm] = useState<TermKey>(currentTermKey());
  const [dueDate, setDueDate] = useState(plusDays(14));
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [busy, setBusy] = useState(false);
  // Invoices created in THIS session, newest first. In-memory only, so it
  // clears on a fresh app open; the Clear button empties it on demand.
  const [recent, setRecent] = useState<Invoice[]>([]);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const student = acad.students.find((s) => s.id === studentId);
  const matches = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return [];
    return acad.students
      .filter((s) => s.fullName.toLowerCase().includes(q) || (s.admissionNumber ?? '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [studentSearch, acad.students]);

  const activeItems = data.feeItems.filter((f) => f.active);
  const subtotal = round2(lines.reduce((a, l) => a + (Number(l.amount) || 0), 0));
  const canIssue = !!student && lines.length > 0 && subtotal > 0 && lines.every((l) => l.label.trim()) && !busy;

  function addCatalog(item: FeeItem) {
    setLines((ls) => [...ls, { feeItemId: item.id, label: item.name, category: item.category, amount: item.amount }]);
  }
  function addCustom() {
    setLines((ls) => [...ls, { label: '', category: 'other', amount: 0 }]);
  }
  function updateLine(i: number, patch: Partial<InvoiceLine>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function removeLine(i: number) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
  }

  async function issue() {
    if (!student || !canIssue) return;
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Issuing invoice…' });
    try {
      const id = `${year}_${term}_${student.id}_x${Date.now().toString(36)}`;
      const seq = data.invoices.filter((i) => i.year === year).length + 1;
      const invoiceNo = formatInvoiceNo(data.settings, { year, term, seq });
      const inv = assembleInvoice({
        id, invoiceNo,
        studentId: student.id, studentName: student.fullName, familyId: student.familyId,
        term, year,
        issuedAt: new Date().toISOString(),
        dueDate: new Date(dueDate).toISOString(),
        lines: lines.map((l) => ({ ...l, label: l.label.trim(), amount: round2(Number(l.amount) || 0) })),
        rules: [],
        status: 'issued',
      });
      await finance.invoices.set(id, inv as unknown as Record<string, unknown>);
      await recordFinanceAudit({ action: 'invoice.manual', actor, subjectId: id, after: { studentId: student.id, invoiceNo, total: inv.total } });
      setSc({ show: true, status: 'saving', message: 'Emailing the invoice to the parent…' });
      const delivery = await deliverInvoice(inv, {
        toEmail: getPrimaryContact(student)?.email,
        studentName: student.fullName,
      });
      const emailNote = delivery.emailed
        ? ` — emailed to ${getPrimaryContact(student)?.email}`
        : delivery.reason
          ? ` — ${delivery.reason}`
          : '';
      setSc({ show: true, status: 'success', message: `${invoiceNo} issued — ${fmtPula(inv.total)} to ${student.fullName}${emailNote}.` });
      setRecent((r) => [inv, ...r]); // surface for verification this session
      setLines([]); // ready for another; keep the student selected
    } catch (e) {
      setSc({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function setInvoiceEmailEnabled(next: boolean) {
    setSavingToggle(true);
    try {
      await finance.settings.update({ invoiceEmailEnabled: next });
      await recordFinanceAudit({
        action: 'settings.invoiceEmailToggle',
        actor,
        subjectId: 'school',
        after: { invoiceEmailEnabled: next },
      });
    } catch (e) {
      setSc({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSavingToggle(false);
    }
  }

  async function downloadPdf(inv: Invoice) {
    try {
      const logo = await loadLogoDataUrl().catch(() => undefined);
      buildInvoicePdf(inv, logo ? { logoDataUrl: logo } : {}).save(`${inv.invoiceNo || inv.id}.pdf`);
    } catch {
      /* a failed render shouldn't break the screen */
    }
  }

  return (
    <div className="space-y-6">
      <GradientHero icon={ReceiptText} title="New invoice" subtitle="Raise a one-off invoice for a single student" />

      {/* subtabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {([['new', 'New invoice', ReceiptText], ['settings', 'Email settings', Settings]] as const).map(
          ([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setSub(id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                sub === id
                  ? 'border-[var(--accent-strong,#0f766e)] text-[var(--accent-link,#0d9488)] dark:text-[var(--accent-link-dark,#5eead4)]'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ),
        )}
      </div>

      {!emailEnabled && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          <AlertTriangle size={15} className="shrink-0" />
          <span>
            Invoice emailing is currently <span className="font-semibold">OFF</span> — invoices are
            created and saved as PDFs but are not emailed to parents.
          </span>
        </div>
      )}

      {sub === 'settings' && (
        <SoftCard>
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-[var(--accent-link,#2d5295)] mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Email invoices to parents</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                When ON, generating an invoice — here, at admission, for exam fees, or from a
                student's fees panel — emails the PDF to the parent. When OFF, the invoice PDF is
                still created and saved; it just isn't emailed. (Bulk billing runs never email.)
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button
                  role="switch"
                  aria-checked={emailEnabled}
                  disabled={savingToggle}
                  onClick={() => setInvoiceEmailEnabled(!emailEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    emailEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      emailEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span
                  className={`text-sm font-medium ${
                    emailEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'
                  }`}
                >
                  {savingToggle ? 'Saving…' : emailEnabled ? 'Emailing is ON' : 'Emailing is OFF'}
                </span>
              </div>
            </div>
          </div>
        </SoftCard>
      )}

      {sub === 'new' && (
      <>
      <SoftCard>
        <div className="space-y-4">
          {/* Student */}
          {!student ? (
            <div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Student</span>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input autoFocus value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} placeholder="Search name or admission no." className={glassFieldClass('py-2 pl-8 w-full')} />
              </div>
              {matches.length > 0 && (
                <div className="mt-1 rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50 overflow-hidden">
                  {matches.map((s) => (
                    <button key={s.id} onClick={() => { setStudentId(s.id); setStudentSearch(''); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center justify-between">
                      <span className="text-gray-800 dark:text-gray-100">{s.fullName}</span>
                      <span className="text-xs text-gray-400">{s.currentClass}{s.admissionNumber ? ` · ${s.admissionNumber}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-xl bg-gray-50 dark:bg-gray-700/40 px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                <UserRound size={15} className="text-gray-400" /> {student.fullName}
                <span className="text-xs font-normal text-gray-500">{student.currentClass}</span>
              </span>
              <button onClick={() => setStudentId('')} className="text-xs text-[var(--accent-link,#2d5295)] hover:underline">change</button>
            </div>
          )}

          {/* Period + due */}
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Year</span>
              <select value={year} onChange={(e) => setYear(e.target.value)} className={glassFieldClass('py-2 w-full')}>
                {SCHOOL.selectableYears.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Term</span>
              <select value={term} onChange={(e) => setTerm(e.target.value as TermKey)} className={glassFieldClass('py-2 w-full')}>
                {TERMS.map((t) => <option key={t} value={t}>{termLabel(t)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Due date</span>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={glassFieldClass('py-2 w-full')} />
            </label>
          </div>

          {/* Catalog quick-add */}
          {activeItems.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Add from catalog</span>
              <div className="flex flex-wrap gap-1.5">
                {activeItems.map((item) => (
                  <button key={item.id} onClick={() => addCatalog(item)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-link,#2d5295)]/10 text-[var(--accent-link,#2d5295)] hover:bg-[var(--accent-link,#2d5295)]/20 dark:text-[var(--accent-link-dark,#93c5fd)]">
                    <Plus size={12} /> {item.name} · {fmtPula(item.amount)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lines */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Invoice lines</span>
              <button onClick={addCustom} className="inline-flex items-center gap-1 text-xs text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]"><Plus size={12} /> custom line</button>
            </div>
            {lines.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-2">No lines yet — add one from the catalog or a custom line.</p>
            ) : (
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <input value={l.label} onChange={(e) => updateLine(i, { label: e.target.value })} placeholder="Description (what is this charge for?)" className={glassFieldClass('py-2 text-sm')} />
                    </div>
                    <div className="w-44 shrink-0">
                      <select value={l.category} onChange={(e) => updateLine(i, { category: e.target.value as FeeCategory })} className={glassFieldClass('py-2 text-sm')}>
                        {(Object.keys(FEE_CATEGORY_LABELS) as FeeCategory[]).map((c) => <option key={c} value={c}>{FEE_CATEGORY_LABELS[c]}</option>)}
                      </select>
                    </div>
                    <div className="w-28 shrink-0">
                      <NumberField value={l.amount} onValue={(n) => updateLine(i, { amount: n })} className={glassFieldClass('py-2 text-sm text-right')} />
                    </div>
                    <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">Total</span>
            <span className="text-lg font-bold text-gray-900 dark:text-white">{fmtPula(subtotal)}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-gray-700">
          <AccentButton icon={CheckCircle2} disabled={!canIssue} onClick={issue}>
            {busy ? 'Issuing…' : 'Issue invoice'}
          </AccentButton>
        </div>
      </SoftCard>

      {recent.length > 0 && (
        <SoftCard>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Created this session</span>
            <button onClick={() => setRecent([])} className="text-xs text-gray-500 hover:text-red-500 dark:text-gray-400">Clear</button>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {recent.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                    {inv.invoiceNo} · {inv.studentName}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(inv.issuedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {fmtPula(inv.total)}
                  </div>
                </div>
                <button
                  onClick={() => downloadPdf(inv)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)] shrink-0"
                >
                  <Download size={13} /> PDF
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-400">This list is just for verification and clears when you reopen the app.</p>
        </SoftCard>
      )}
      </>
      )}

      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />
    </div>
  );
}

/* App.tsx imports this screen by name. The default export is the same component. */
export { ManualInvoiceScreen };
